import { type ISection } from "src/interfaces";

import { type ILandscapeRow } from "../../generator/LandscapeOrientation/landscape.types";
import { deepClone, normalizeDate } from "../../utils";
import { IGuernseyRow } from "../../components/shared/GuernseyMulti/Guernsey.types";
/**
 * Inserts values into the provided sections based on the given ID-value mapping.
 *
 * @param idValueObject - A record where keys are IDs and values are the corresponding values to insert.
 * @param sections - An array of sections to process and insert values into.
 * @param markAsSaved - Optional boolean to mark all rows as saved in grayOldRows sections.
 * @returns A new array of sections with the values inserted.
 */
export const insertValues = (
  idValueObject: Record<string, string>,
  sections: ISection[],
  markAsSaved?: boolean,
) => {
  const result: ISection[] = [];
  sections.forEach(sec => {
    if (sec.subSections) {
      const localSecs: any = [];
      sec.subSections.forEach(subSec => {
        localSecs.push(
          convertSection(
            subSec as any,
            idValueObject,
            true,
            markAsSaved && sec.grayOldRows,
            markAsSaved,
          ),
        );
      });
      result.push(deepClone({ ...sec, subSections: localSecs }));
    } else if (sec.shared && sec.sections) {
      const localSecs: any = [];
      sec.sections.forEach(sec => {
        localSecs.push(
          convertSection(
            sec,
            idValueObject,
            undefined,
            markAsSaved && sec.grayOldRows,
            markAsSaved,
          ),
        );
      });
      result.push(deepClone({ ...sec, sections: localSecs }));
    } else {
      result.push(
        deepClone(
          convertSection(
            sec,
            idValueObject,
            undefined,
            markAsSaved && sec.grayOldRows,
            markAsSaved,
          ),
        ),
      );
    }
  });
  return result;
};
/**
 * Converts a section by inserting values into its rows and columns based on the given ID-value mapping.
 *
 * @param sec - The section to process.
 * @param idValueObject - A record where keys are IDs and values are the corresponding values to insert.
 * @param inSubSect - A boolean indicating if the section is part of a subsection. Defaults to `false`.
 * @param markAsSaved - Optional boolean to mark rows as saved in the savedRows array.
 * @param manualSave - Whether this conversion is for a manual save; clears
 *   `unsavedRowAdded` so it round-trips through the backend like `savedRows`.
 * @returns A new section with the values inserted.
 */
const convertSection = (
  sec: ISection,
  idValueObject: any,
  inSubSect?: boolean,
  markAsSaved?: boolean,
  manualSave?: boolean,
) => {
  const section = structuredClone(sec);

  /**
   * Callback function to process each column in a row.
   *
   * @param row - The row containing columns to process.
   */
  const forEachColCallback = (row: any) => {
    for (const fieldId in row.cols) {
      if (row.cols[fieldId].exclusive) {
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          value:
            idValueObject[
              fieldId + "-exclusive-" + row.cols[fieldId].exclusive
            ],
        };
      } else if (row.cols[fieldId].exclusiveUser) {
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          value:
            idValueObject[
              fieldId + "-exclusiveUser-" + row.cols[fieldId].exclusiveUser
            ],
        };
      } else if (
        inSubSect &&
        row.cols[fieldId].type === "split" &&
        row.cols[fieldId].unit === "sub"
      ) {
        const contentCols = row.cols[fieldId]?.cols;
        if (contentCols) {
          for (const nestedId in contentCols) {
            contentCols[nestedId].value = idValueObject[nestedId];
          }
        }
      } else if (
        "content" in row.cols[fieldId] &&
        row.cols[fieldId].unit !== "guernseyMulti"
      ) {
        const contentCols = row.cols[fieldId].content?.cols;
        if (contentCols) {
          for (const nestedId in contentCols) {
            if (contentCols[nestedId].exclusive) {
              contentCols[nestedId] = {
                ...contentCols[nestedId],
                value:
                  idValueObject[
                    nestedId + "-exclusive-" + contentCols[nestedId].exclusive
                  ],
              };
            } else if (contentCols[nestedId].exclusiveUser) {
              contentCols[nestedId] = {
                ...contentCols[nestedId],
                value:
                  idValueObject[
                    nestedId +
                      "-exclusiveUser-" +
                      contentCols[nestedId].exclusiveUser
                  ],
              };
            } else {
              contentCols[nestedId] = {
                ...contentCols[nestedId],
                value: idValueObject[nestedId],
              };
            }
          }
        }
      } else if (
        (row.cols[fieldId].unit === "large/text" ||
          row.cols[fieldId].unit === "bool/attach/text") &&
        "list" in row.cols[fieldId]
      ) {
        const list = idValueObject[fieldId] ?? [];
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          list,
        };
      } else if (
        row.cols[fieldId].unit === "vital_list" &&
        "list" in row.cols[fieldId]
      ) {
        const list = idValueObject[fieldId] ? idValueObject[fieldId] : [];
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          list,
        };
      } else if (
        row.cols[fieldId].feature === "logDiff" &&
        row.cols[fieldId].class === "logResults"
      ) {
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          value: idValueObject[fieldId],
          defaultValue:
            row.cols[fieldId].defaultValue ?? idValueObject[fieldId],
        };
      } else if (row.cols[fieldId].unit === "date") {
        if (idValueObject[fieldId]) {
          row.cols[fieldId].value = normalizeDate(
            idValueObject[fieldId],
          )?.toISOString();
        } else {
          row.cols[fieldId].value = idValueObject[fieldId];
        }
      } else if (row.cols[fieldId].unit === "guernseyMulti") {
        const filledContent: any = {};

        Object.entries(row.cols[fieldId].content).forEach(([key, rows]) => {
          if (key === "cols") return;

          filledContent[key] = (rows as IGuernseyRow[]).map(item => {
            const newCols: any = {};
            Object.entries(item.cols).forEach(([key, col]) => {
              let value = idValueObject[key];
              if (col.unit === "date") {
                value = normalizeDate(value)?.toISOString();
              }
              newCols[key] = {
                ...col,
                value,
              };
            });

            return { ...item, cols: newCols };
          });
        });
        row.cols[fieldId].content = filledContent;
        row.cols[fieldId].value = idValueObject[fieldId];
      } else {
        row.cols[fieldId] = {
          ...row.cols[fieldId],
          value: idValueObject[fieldId],
        };
      }
    }
  };

  section.rows.forEach((row: any | ILandscapeRow, index) => {
    if (row.cols) {
      forEachColCallback(row);
    } else if (row.subrows) {
      row.subrows.forEach((subrow: any) => {
        forEachColCallback(subrow);
      });
    } else if (row.items) {
      Object.values(row.items).forEach((item: any) => {
        forEachColCallback(item);
      });
    }
  });

  if (markAsSaved && sec.grayOldRows) {
    section.savedRows = section.rows.map(() => true);
  }

  if (manualSave) {
    section.unsavedRowAdded = false;
  }

  return section;
};
